import { Injectable, signal, inject } from '@angular/core';
import { ApplicationConfig } from '../../../core/config/application.config';
import {
  Chargify,
  ChargifyConfig,
  ChargifyToken,
  ChargifyError,
  ChargifySuccessCallback,
  ChargifyErrorCallback,
  ChargifyPaymentType,
} from '../model/chargify';
import { PaymentResult } from '../model/payment-result';
import { ErrorHandlerUtil } from '../../../shared/util/error-handler.util';
import { ERROR_MESSAGES } from '../../../shared/constant/error-messages.constant';
import { BillingAddress } from '../../subscription/model/billing-address';

@Injectable({ providedIn: 'root' })
export class ChargifyService {
  private readonly errorHandler = inject(ErrorHandlerUtil);
  private chargify: Chargify | null = null;
  private isInitialized = signal(false);

  public initialize(selector?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!ApplicationConfig.chargify) {
        const error = this.errorHandler.getTranslatedMessage(
          ERROR_MESSAGES.CHARGIFY.CHARGIFY_CONFIG_NOT_FOUND
        );
        reject(new Error(error));
        return;
      }

      if (typeof window === 'undefined') {
        const error = this.errorHandler.getTranslatedMessage(
          ERROR_MESSAGES.CHARGIFY.CHARGIFY_NOT_LOADED
        );
        reject(new Error(error));
        return;
      }

      // Check if Chargify script is loaded
      if (!window.Chargify) {
        const error = this.errorHandler.getTranslatedMessage(
          ERROR_MESSAGES.CHARGIFY.CHARGIFY_NOT_LOADED
        );
        reject(new Error(error));
        return;
      }

      try {
        this.chargify = new window.Chargify();

        // Check if load method exists
        if (typeof this.chargify.load !== 'function') {
          reject(new Error('Chargify load method not available'));
          return;
        }
      } catch (error) {
        reject(error);
        return;
      }

      const config: ChargifyConfig = {
        publicKey: ApplicationConfig.chargify.publicKey,
        type: ChargifyPaymentType.CARD,
        serverHost: ApplicationConfig.chargify.serverHost,
        style: {
          field: {
            backgroundColor: '#FFFFFF',
            fontSize: '1rem',
            color: '#494850',
            fontFamily: 'Lato, sans-serif',
            fontWeight: '400',
            transition: 'border-color 0.2s',
            boxSizing: 'border-box',
          },
          input: {
            display: 'flex',
            verticalAlign: 'middle',
            border: '1px solid #D8D8F6',
            borderRadius: '8px',
            backgroundColor: 'transparent',
            fontSize: '1rem',
            color: '#494850',
            fontFamily: 'Lato, sans-serif',
            fontWeight: '400',
            outline: 'none',
            width: '100%',
            height: '56px',
            boxSizing: 'border-box',
          },
          label: {
            fontSize: '0.875rem',
            color: '#494850',
            fontFamily: 'Lato, sans-serif',
            fontWeight: '500',
            marginBottom: '8px',
            display: 'block',
          },
          message: {
            fontSize: '0.75rem',
            color: '#FF4D4F',
            fontFamily: 'Lato, sans-serif',
            fontWeight: '400',
            marginTop: '0.25rem',
          },
        },
      };

      // Use iframe integration for secure payment fields
      if (selector) {
        config.selector = selector;
      } else {
        config.fields = {
          number: {
            selector: '#chargify-card-number',
            label: 'Credit Card Number*',
            placeholder: 'Card Number',
            required: true,
          },
          cvv: {
            selector: '#chargify-cvv',
            label: 'CVV*',
            placeholder: '000',
            required: true,
            maxlength: '4',
          },
          firstName: {
            selector: '#chargify-first-name',
            label: 'First name on Account*',
            placeholder: 'John',
            required: true,
            maxlength: '50',
          },
          lastName: {
            selector: '#chargify-last-name',
            label: 'Last name on Account*',
            placeholder: 'Doe',
            required: true,
            maxlength: '50',
          },
          month: {
            selector: '#chargify-expiration-month',
            label: 'Expiration Month*',
            placeholder: 'MM',
            required: true,
            maxlength: '2',
          },
          year: {
            selector: '#chargify-expiration-year',
            label: 'Expiration Year*',
            placeholder: 'YYYY',
            required: true,
            maxlength: '4',
          },
        };
      }

      let attempts = 0;
      const maxAttempts = 60; // 30 seconds max

      const checkIframesLoaded = () => {
        attempts++;
        const requiredFields = [
          '#chargify-card-number',
          '#chargify-cvv',
          '#chargify-first-name',
          '#chargify-last-name',
          '#chargify-expiration-month',
          '#chargify-expiration-year',
        ];

        const fieldStatus = requiredFields.map(selector => {
          const container = document.querySelector(selector);
          const hasIframe = container && container.querySelector('iframe');
          return { selector, hasIframe, container: !!container };
        });

        const allFieldsLoaded = fieldStatus.every(field => field.hasIframe);

        if (allFieldsLoaded) {
          this.isInitialized.set(true);
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Timeout waiting for Chargify iframes to load'));
        } else {
          setTimeout(checkIframesLoaded, 500);
        }
      };

      // Verify that all required field containers exist
      const requiredFields = [
        '#chargify-card-number',
        '#chargify-cvv',
        '#chargify-first-name',
        '#chargify-last-name',
        '#chargify-expiration-month',
        '#chargify-expiration-year',
      ];

      const missingFields = requiredFields.filter(
        selector => !document.querySelector(selector)
      );
      if (missingFields.length > 0) {
        reject(
          new Error(`Missing field containers: ${missingFields.join(', ')}`)
        );
        return;
      }

      try {
        this.chargify.load(config);
      } catch (error) {
        reject(error);
        return;
      }

      // Start checking for iframe loading
      setTimeout(checkIframesLoaded, 500);
    });
  }

  public getToken(
    form: HTMLFormElement,
    billingAddress?: BillingAddress
  ): Promise<PaymentResult> {
    if (!this.chargify || !this.isInitialized()) {
      return Promise.reject(
        new Error(
          this.errorHandler.getTranslatedMessage(
            ERROR_MESSAGES.CHARGIFY.CHARGIFY_NOT_INITIALIZED
          )
        )
      );
    }

    const chargifyInstance = this.chargify;

    return new Promise(resolve => {
      const successCallback: ChargifySuccessCallback = (
        token: ChargifyToken | string
      ) => {
        // Handle both string and object token formats
        let tokenString: string;
        if (typeof token === 'string') {
          tokenString = token;
        } else {
          tokenString = token.token;
        }

        resolve({
          success: true,
          token: tokenString,
          paymentMethod: undefined,
        });
      };

      const errorCallback: ChargifyErrorCallback = (error: ChargifyError) => {
        resolve({
          success: false,
          error:
            error.errors ||
            error.message ||
            this.errorHandler.getTranslatedMessage(
              ERROR_MESSAGES.CHARGIFY.PAYMENT_TOKEN_FAILED
            ),
        });
      };

      // Add billing address fields as hidden form fields if provided
      if (billingAddress) {
        this.addBillingAddressFieldsToForm(form, billingAddress);
      }

      chargifyInstance.token(form, successCallback, errorCallback);
    });
  }

  private addBillingAddressFieldsToForm(
    form: HTMLFormElement,
    billingAddress: BillingAddress
  ): void {
    // Remove any existing billing address fields
    const existingFields = form.querySelectorAll('input[data-chargify]');
    existingFields.forEach(field => field.remove());

    // Add billing address fields to the form using the correct data-chargify attributes
    const billingFields = [
      { dataChargify: 'address', value: billingAddress.addressLine1 },
      { dataChargify: 'address2', value: billingAddress.addressLine2 || '' },
      { dataChargify: 'city', value: billingAddress.city },
      { dataChargify: 'state', value: billingAddress.state },
      { dataChargify: 'country', value: billingAddress.country },
      { dataChargify: 'zip', value: billingAddress.postalCode },
    ];

    billingFields.forEach(({ dataChargify, value }) => {
      const fieldContainer = document.createElement('div');
      fieldContainer.className = 'host-field';

      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.className = 'host-input';
      hiddenField.setAttribute('data-chargify', dataChargify);
      hiddenField.value = value;

      fieldContainer.appendChild(hiddenField);
      form.appendChild(fieldContainer);
    });
  }
}
